import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Ban, CheckCircle, AtSign, Pencil, Save, X } from 'lucide-react';

export default function AdminUserDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();

  const [user,     setUser]     = useState(null);
  const [listings, setListings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [blocking, setBlocking] = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    Promise.all([
      api.get('/admin/users/' + id),
      api.get('/admin/users/' + id + '/contacts'),
    ])
      .then(([uRes, cRes]) => {
        const u = uRes.data.user;
        setUser(u);
        setListings(uRes.data.listings);
        setContacts(cRes.data.contacts);
        setEditForm({
          name:           u.name           || '',
          username:       u.username       || '',
          rollNumber:     u.rollNumber     || '',
          callNumber:     u.callNumber     || '',
          whatsappNumber: u.whatsappNumber || '',
          location:       u.location       || '',
          gender:         u.gender         || 'prefer_not_to_say',
        });
      })
      .catch(() => toast.error('Could not load user.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleBlock = async () => {
    if (!window.confirm(user.isBlocked ? 'Unblock this user?' : 'Block this user?')) return;
    setBlocking(true);
    try {
      const { data } = await api.put('/admin/users/' + id + '/block');
      setUser(u => ({ ...u, isBlocked: data.isBlocked }));
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally { setBlocking(false); }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/users/' + id + '/edit', editForm);
      setUser(data.user);
      setEditing(false);
      toast.success('User details updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update.');
    } finally { setSaving(false); }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name:           user.name           || '',
      username:       user.username       || '',
      rollNumber:     user.rollNumber     || '',
      callNumber:     user.callNumber     || '',
      whatsappNumber: user.whatsappNumber || '',
      location:       user.location       || '',
      gender:         user.gender         || 'prefer_not_to_say',
    });
    setEditing(false);
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!user)   return <div className="empty-state"><h4>User not found</h4></div>;

  return (
    <div className="fade-in">

      <button className="btn btn-ghost btn-icon" style={{ marginBottom: 24 }} onClick={() => navigate(-1)}>
        <ArrowLeft size={20} />
      </button>

      {/* ── User info card ── */}
      <div className="card" style={{ padding: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {user.avatar
              ? <img src={user.avatar} alt="" className="avatar avatar-lg" />
              : <div className="avatar-placeholder avatar-lg" style={{ fontSize: '1.4rem' }}>{user.name?.[0]}</div>
            }
            <div>
              <h3 style={{ marginBottom: 4 }}>{user.name}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <AtSign size={13} style={{ color: 'var(--accent)' }} />
                <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.9rem' }}>
                  {user.username || 'no username'}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                {user.email}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {user.isAdmin   && <span className="badge badge-info">Admin</span>}
                {user.isBlocked && <span className="badge badge-danger">Blocked</span>}
                {!user.isAdmin && !user.isBlocked && <span className="badge badge-success">Active</span>}
                {!user.profileComplete && <span className="badge badge-warning">Incomplete Profile</span>}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Sells',     value: user.sellCount      ?? 0, color: 'var(--accent)'  },
              { label: 'Listings',  value: user.activeListings ?? 0, color: 'var(--success)' },
              { label: 'Contacts',  value: user.buyCount       ?? 0, color: 'var(--info)'    },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: s.color }}>
                  {s.value}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Edit / View mode toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-4)' }}>
          {!editing ? (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={14} /> Edit Details
              </button>
              {!user.isAdmin && (
                <button
                  className={'btn btn-sm ' + (user.isBlocked ? 'btn-success' : 'btn-danger')}
                  onClick={handleBlock}
                  disabled={blocking}
                >
                  {blocking
                    ? <div className="spinner" style={{ width: 14, height: 14 }} />
                    : user.isBlocked
                      ? <><CheckCircle size={14} /> Unblock</>
                      : <><Ban size={14} /> Block</>
                  }
                </button>
              )}
            </>
          ) : (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSaveEdit} disabled={saving}>
                {saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : <><Save size={14} /> Save Changes</>}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                <X size={14} /> Cancel
              </button>
            </>
          )}
        </div>

        {/* Detail fields */}
        {editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            {[
              { label: 'Full Name',       key: 'name',           type: 'text',  placeholder: 'Full name' },
              { label: 'Username',        key: 'username',       type: 'text',  placeholder: 'username' },
              { label: 'Roll Number',     key: 'rollNumber',     type: 'text',  placeholder: '10–13 chars' },
              { label: 'Call Number',     key: 'callNumber',     type: 'tel',   placeholder: '10-digit' },
              { label: 'WhatsApp Number', key: 'whatsappNumber', type: 'tel',   placeholder: '10-digit' },
              { label: 'Location',        key: 'location',       type: 'text',  placeholder: 'Campus location' },
            ].map(field => (
              <div key={field.key} className="form-group">
                <label className="label" style={{ fontSize: '0.78rem' }}>{field.label}</label>
                <input
                  className="input"
                  style={{ fontSize: '0.875rem' }}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={editForm[field.key] || ''}
                  onChange={e => setEditForm(f => ({ ...f, [field.key]: e.target.value }))}
                  maxLength={field.key === 'rollNumber' ? 13 : undefined}
                />
              </div>
            ))}
            {/* Gender — separate because it's a select */}
            <div className="form-group">
              <label className="label" style={{ fontSize: '0.78rem' }}>Gender</label>
              <select
                className="input"
                style={{ fontSize: '0.875rem' }}
                value={editForm.gender || 'prefer_not_to_say'}
                onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}
              >
                <option value="prefer_not_to_say">Prefer not to say</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            {[
              { label: 'Roll Number',     value: user.rollNumber     || '—' },
              { label: 'Call Number',     value: user.callNumber     || '—' },
              { label: 'WhatsApp',        value: user.whatsappNumber || '—' },
              { label: 'Location',        value: user.location       || '—' },
              { label: 'Gender',          value: { male: 'Male', female: 'Female', other: 'Other', prefer_not_to_say: 'Prefer not to say' }[user.gender] || '—' },
              { label: 'University',      value: user.university     || '—' },
              { label: 'Joined',          value: new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
            ].map(item => (
              <div key={item.label} style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Listings ── */}
      <h3 style={{ marginBottom: 16 }}>
        Listings
        <span className="badge badge-neutral" style={{ fontSize: '0.78rem', marginLeft: 8 }}>{listings.length}</span>
      </h3>

      <div className="admin-table-wrap" style={{ marginBottom: 36 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Price</th>
              <th>Category</th>
              <th>Status</th>
              <th>Reach</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listings.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>No listings yet</td></tr>
            ) : listings.map(p => (
              <tr key={p._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {p.images?.[0]
                      ? <img src={p.images[0]} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 40, height: 40, background: 'var(--bg-2)', borderRadius: 8 }} />
                    }
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.title}</span>
                  </div>
                </td>
                <td style={{ fontWeight: 700, color: 'var(--accent)' }}>₹{p.price.toLocaleString()}</td>
                <td><span className={'badge badge-' + p.category}>{p.category}</span></td>
                <td>
                  <span className={'badge ' + (
                    p.status === 'active'            ? 'badge-success' :
                    p.status === 'sold'              ? 'badge-info'    :
                    p.status === 'removed_by_admin'  ? 'badge-danger'  : 'badge-neutral'
                  )}>
                    {p.status === 'active' ? 'Active' : p.status === 'sold' ? 'Sold' :
                     p.status === 'removed_by_admin' ? 'Removed (Admin)' : 'Removed (Self)'}
                  </span>
                </td>
                <td>{p.contactCount ?? 0}</td>
                <td>
                  <Link className="btn btn-ghost btn-sm" to={'/admin/products/' + p._id}>View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Contacts ── */}
      <h3 style={{ marginBottom: 16 }}>
        Buyer Contacts
        <span className="badge badge-neutral" style={{ fontSize: '0.78rem', marginLeft: 8 }}>{contacts.length}</span>
      </h3>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Buyer</th>
              <th>Username</th>
              <th>Roll No.</th>
              <th>Product</th>
              <th>WhatsApp</th>
              <th>Call</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>No contacts yet</td></tr>
            ) : contacts.map(c => (
              <tr key={c._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="avatar-placeholder" style={{ width: 28, height: 28, fontSize: '0.72rem', flexShrink: 0 }}>
                      {c.buyerSnapshot?.name?.[0] || '?'}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.buyerSnapshot?.name || '—'}</span>
                  </div>
                </td>
                <td>
                  <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.82rem' }}>
                    {c.buyerSnapshot?.username ? '@' + c.buyerSnapshot.username : '—'}
                  </span>
                </td>
                <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{c.buyerSnapshot?.rollNumber || '—'}</td>
                <td style={{ fontSize: '0.82rem' }}>{c.productSnapshot?.title || '—'}</td>
                <td style={{ fontSize: '0.82rem' }}>{c.buyerSnapshot?.whatsappNumber || '—'}</td>
                <td style={{ fontSize: '0.82rem' }}>{c.buyerSnapshot?.callNumber || '—'}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}